import { WarningFilled } from '@ant-design/icons'
import { required as requiredRule, composeValidators, email as emailRule } from '@community_dev/pixels'
import { RoleGroup } from '@community_dev/types/lib/api/v1/Roles'
import { Button, Input, Modal, Row, Tooltip, Typography, notification } from 'antd'
import type { JSX } from 'react'
import { Field, Form } from 'react-final-form'

import { RoleFields } from './RoleFields'

import { Client, Role, logOutTeamMember } from 'api/clients'
import { CAPABILITIES } from 'constants/capabilities'
import { SeatRoles } from 'constants/roles'
import { useHasCapability } from 'hooks/useCapabilities'
import { StyledModal } from 'shared/components/Modal'

export type TeamMemberValue = {
  firstName: string
  lastName: string
  email: string
  roles: Role[]
  id: string
  seatMappingId: string
}

export type TeamMemberEditModalProps = {
  onSubmit(value: TeamMemberValue): any
  onCancel(): any
  allRoles: Role[]
  isDownloadEnabled: boolean
  client: Client
  value: TeamMemberValue
}

export function TeamMemberEditModal({
  onSubmit,
  onCancel,
  value,
  isDownloadEnabled,
  client,
  allRoles,
}: TeamMemberEditModalProps): JSX.Element {
  const canLogOutUser = useHasCapability(CAPABILITIES.ADMIN.CLIENT.LOGOUT)
  const isOwner = value.roles.some((role) => role.name === SeatRoles.OWNER)
  const primaryRoles = allRoles.filter((role) => role.group === RoleGroup.PRIMARY)
  async function onLogout(member: TeamMemberValue) {
    try {
      await logOutTeamMember({ memberId: member.id })
      notification.success({ message: `${member.firstName} ${member.lastName} is now logged out.` })
    } catch (e) {
      notification.error({ message: 'There was an error logging out the seat.' })
    }
  }
  return (
    <Form
      initialValues={value}
      onSubmit={onSubmit}
      render={({ handleSubmit, values, submitting, valid }) => (
        <StyledModal
          cancelText={null}
          confirmLoading={submitting}
          footer={
            <Row justify="space-between">
              <Tooltip title={!canLogOutUser && 'Your account doesn’t have permission to log out a member'}>
                <Button
                  danger
                  disabled={!canLogOutUser}
                  onClick={() => {
                    const confirm = Modal.confirm({
                      type: 'error',
                      icon: <WarningFilled color="red" />,
                      title: `Are you sure you want to log ${values.firstName} ${values.lastName}?`,
                      content: (
                        <>
                          <p>
                            The Team Member will be logged out of all their accounts on Community, not just this
                            specific one.
                          </p>
                          <p>
                            Any API Tokens created by this user will also be invalidated (including on other clients
                            they have access to).
                          </p>
                        </>
                      ),
                      okType: 'danger',
                      okButtonProps: { danger: true, content: 'Yes, log out all members.' },
                      onOk: () => {
                        onLogout(value)
                        confirm.destroy()
                      },
                    })
                  }}
                  size="large"
                >
                  Log out {value.firstName}
                </Button>
              </Tooltip>
              <Button
                data-testid="team-save-button"
                disabled={!valid}
                key="submit"
                loading={submitting}
                onClick={handleSubmit}
                size="large"
                type="primary"
              >
                Save
              </Button>
            </Row>
          }
          onCancel={onCancel}
          open={true}
          title="Edit Team Member"
        >
          <form onSubmit={handleSubmit}>
            <Field<string> name="firstName" validate={requiredRule}>
              {({ input, meta }) => (
                <>
                  <label htmlFor="firstName">First name</label>
                  <Input id="firstName" size="large" type="text" {...input} />
                  {!meta.active && meta.touched && meta.modified && meta.error && (
                    <Typography.Text type="danger">First name {meta.error}</Typography.Text>
                  )}
                </>
              )}
            </Field>

            <Field<string> name="lastName">
              {({ input, meta }) => (
                <>
                  <label htmlFor="lastName">Last name</label>
                  <Input id="lastName" size="large" type="text" {...input} />
                  {!meta.active && meta.touched && meta.modified && meta.error && (
                    <Typography.Text type="danger">Last name {meta.error}</Typography.Text>
                  )}
                </>
              )}
            </Field>

            <Field<string> name="email" validate={composeValidators(requiredRule, emailRule)}>
              {({ input, meta }) => (
                <>
                  <label htmlFor="email">Email Address</label>
                  <Input id="email" size="large" type="email" {...input} />
                  {!meta.active && meta.touched && meta.modified && meta.error && (
                    <Typography.Text type="danger">Email {meta.error}</Typography.Text>
                  )}
                </>
              )}
            </Field>
            <Field<Role[]> name="roles">
              {({ input }) => (
                <RoleFields
                  allRoles={allRoles}
                  client={client}
                  isDownloadEnabled={isDownloadEnabled}
                  onChange={input.onChange}
                  primaryRoleLockedReason={
                    isOwner ? (
                      <Typography.Text type="secondary">
                        Ownership cannot be removed, only transferred to another account.
                      </Typography.Text>
                    ) : null
                  }
                  primaryRoles={primaryRoles}
                  selectedRoles={input.value}
                />
              )}
            </Field>
          </form>
        </StyledModal>
      )}
    />
  )
}
